import type { PreVisitQuestionType } from '@/constants/preVisitQuestionTypes';

export interface PreVisitQuestionOptionApiResource {
  code: string;
  label_en: string;
  label_pl: string;
}

export interface PreVisitQuestionOption {
  code: string;
  labelEn: string;
  labelPl: string;
}

export interface PreVisitQuestionOptionDraft {
  code: string | null;
  labelEn: string;
  labelPl: string;
}

export interface PreVisitQuestionApiResource {
  code: string;
  question_type: PreVisitQuestionType;
  label_en: string;
  label_pl: string;
  help_en: string | null;
  help_pl: string | null;
  options: PreVisitQuestionOptionApiResource[];
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
}

export interface PreVisitQuestion {
  code: string;
  questionType: PreVisitQuestionType;
  labelEn: string;
  labelPl: string;
  helpEn: string | null;
  helpPl: string | null;
  options: PreVisitQuestionOption[];
  isRequired: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface PreVisitQuestionDraft {
  code: string | null;
  questionType: PreVisitQuestionType;
  labelEn: string;
  labelPl: string;
  helpEn: string | null;
  helpPl: string | null;
  options: PreVisitQuestionOptionDraft[];
  isRequired: boolean;
}

export interface PreVisitQuestionsApiResource {
  questions: PreVisitQuestionApiResource[];
}

export interface PreVisitPhotoApiResource {
  id: string;
  question_code: string;
  filename: string;
  content_type: string;
  url: string;
}

export interface PreVisitPhoto {
  id: string;
  questionCode: string;
  filename: string;
  contentType: string;
  url: string;
}

export interface PreVisitAnswerApiResource {
  question_code: string;
  question_type: PreVisitQuestionType;
  label: string;
  value: string | string[] | null;
  photos: PreVisitPhotoApiResource[];
}

export interface PreVisitAnswer {
  questionCode: string;
  questionType: PreVisitQuestionType;
  label: string;
  value: string | string[] | null;
  photos: PreVisitPhoto[];
}

export interface PreVisitAnswerInput {
  questionCode: string;
  value: string | string[] | null;
}

function mapPreVisitQuestionOption(
  resource: PreVisitQuestionOptionApiResource
): PreVisitQuestionOption {
  return {
    code: resource.code,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
  };
}

function mapPreVisitQuestion(resource: PreVisitQuestionApiResource): PreVisitQuestion {
  return {
    code: resource.code,
    questionType: resource.question_type,
    labelEn: resource.label_en,
    labelPl: resource.label_pl,
    helpEn: resource.help_en,
    helpPl: resource.help_pl,
    options: resource.options.map(mapPreVisitQuestionOption),
    isRequired: resource.is_required,
    isActive: resource.is_active,
    sortOrder: resource.sort_order,
  };
}

export function mapApiResourceToPreVisitQuestions(
  resource: PreVisitQuestionsApiResource
): PreVisitQuestion[] {
  return resource.questions.map(mapPreVisitQuestion);
}

export function mapPreVisitQuestionDrafts(
  drafts: PreVisitQuestionDraft[]
): Record<string, unknown> {
  return {
    questions: drafts.map(function toApiQuestion(draft) {
      return {
        code: draft.code,
        question_type: draft.questionType,
        label_en: draft.labelEn,
        label_pl: draft.labelPl,
        help_en: draft.helpEn,
        help_pl: draft.helpPl,
        options: draft.options.map(function toApiOption(option) {
          return { code: option.code, label_en: option.labelEn, label_pl: option.labelPl };
        }),
        is_required: draft.isRequired,
      };
    }),
  };
}

export function mapApiResourceToPreVisitPhoto(resource: PreVisitPhotoApiResource): PreVisitPhoto {
  return {
    id: resource.id,
    questionCode: resource.question_code,
    filename: resource.filename,
    contentType: resource.content_type,
    url: resource.url,
  };
}

export function mapApiResourceToPreVisitAnswer(
  resource: PreVisitAnswerApiResource
): PreVisitAnswer {
  return {
    questionCode: resource.question_code,
    questionType: resource.question_type,
    label: resource.label,
    value: resource.value,
    photos: resource.photos.map(mapApiResourceToPreVisitPhoto),
  };
}

export function mapApiResourceToPreVisitAnswers(
  resources: PreVisitAnswerApiResource[]
): PreVisitAnswer[] {
  return resources.map(mapApiResourceToPreVisitAnswer);
}

export function mapPreVisitAnswerInputs(
  inputs: PreVisitAnswerInput[]
): Record<string, unknown>[] {
  return inputs.map(function toApiAnswer(input) {
    return { question_code: input.questionCode, value: input.value };
  });
}

export function buildPreVisitQuestionsByCode(
  questions: PreVisitQuestion[]
): Record<string, PreVisitQuestion> {
  const questionsByCode: Record<string, PreVisitQuestion> = {};
  for (const question of questions) {
    questionsByCode[question.code] = question;
  }
  return questionsByCode;
}
